import { useCallback, useEffect, useMemo, useState } from "react";
import { Building2, Edit, Image as ImageIcon, KeyRound, Loader2, Mail, Plus, ShieldCheck, Trash2, UserPlus } from "lucide-react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import { apiCreate, apiList, apiUpdate } from "@/lib/api";
import { UserRole, delegablePermissions, permissionPresets, roleLabels, rolePermissions, sanitizeExtraPermissions } from "@/lib/permissions";
import { useModulosSaas } from "@/hooks/useModulosSaas";
import { useAutomaticBrazilLookup } from "@/hooks/useAutomaticBrazilLookup";
import { lookupCep, lookupCnpj, maskCep, maskCnpj, maskPhone } from "@/lib/brasil";
import { toast } from "sonner";

type ClinicaStatus = "Ativa" | "Inativa";
type UsuarioStatus = "Ativo" | "Bloqueado";

type ClinicaSaas = {
  id: string;
  cliente_id: string;
  nome: string;
  razao_social?: string;
  nome_fantasia?: string;
  cnpj?: string;
  telefone?: string;
  email?: string;
  endereco?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  situacao_cadastral?: string;
  cnae_principal?: string;
  natureza_juridica?: string;
  porte?: string;
  data_abertura?: string;
  logo_base64?: string;
  observacoes?: string;
  status: ClinicaStatus;
};

type UsuarioClinica = {
  id: string;
  cliente_id: string;
  clinica_id: string;
  clinicas_ids?: string[];
  funcionario_id?: string;
  dentista_id?: string;
  dentistas_por_clinica?: Record<string, string>;
  nome: string;
  email: string;
  papel: UserRole;
  status: UsuarioStatus;
  login_user_id?: string;
  permissoes_extra?: string[];
  permissoes_por_clinica?: Record<string, string[]>;
  perfil_personalizado_id?: string;
  exigir_troca_senha?: boolean;
  mfa_obrigatorio?: boolean;
  convite_enviado_em?: string;
  ultimo_login_em?: string;
};

type PerfilAcesso = {
  id: string;
  cliente_id: string;
  nome: string;
  papel_base: UserRole;
  permissoes_extra: string[];
  permissoes_por_clinica?: Record<string, string[]>;
  ativo: boolean;
};

type SessaoAtiva = {
  id: string;
  usuario_email: string;
  usuario_nome?: string;
  login_user_id?: string;
  ip?: string;
  user_agent?: string;
  status: "Ativa" | "Encerrada";
  created_at?: string;
  encerrada_em?: string;
};

type ClienteSaas = {
  id: string;
  email_responsavel?: string;
  login_user_id?: string;
};

type Funcionario = {
  id: string;
  nome: string;
  cargo?: string;
  telefone?: string;
  email: string;
  status: string;
};

type Dentista = {
  id: string;
  nome: string;
  cro?: string;
  especialidade?: string;
  email: string;
  status: string;
};

const emptyClinica: Omit<ClinicaSaas, "id"> = {
  cliente_id: "",
  nome: "",
  razao_social: "",
  nome_fantasia: "",
  cnpj: "",
  telefone: "",
  email: "",
  endereco: "",
  numero: "",
  complemento: "",
  bairro: "",
  cidade: "",
  estado: "",
  cep: "",
  situacao_cadastral: "",
  cnae_principal: "",
  natureza_juridica: "",
  porte: "",
  data_abertura: "",
  logo_base64: "",
  observacoes: "",
  status: "Ativa",
};

const emptyUsuario: Omit<UsuarioClinica, "id"> & { senha?: string } = {
  cliente_id: "",
  clinica_id: "",
  clinicas_ids: [],
  funcionario_id: "",
  dentista_id: "",
  nome: "",
  email: "",
  papel: "recepcao",
  status: "Ativo",
  permissoes_extra: [],
  permissoes_por_clinica: {},
  perfil_personalizado_id: "",
  exigir_troca_senha: true,
  mfa_obrigatorio: false,
  senha: "",
};

const normalizeEmail = (email?: string) => (email || "").trim().toLowerCase();

const permissionsForSelectedClinics = (
  clinicaIds: string[],
  globalPermissions: string[],
  currentMap: Record<string, string[]> = {}
) => clinicaIds.reduce<Record<string, string[]>>((map, clinicaId) => {
  map[clinicaId] = sanitizeExtraPermissions([
    ...(currentMap[clinicaId] || []),
    ...globalPermissions,
  ]);
  return map;
}, {});

export function ClinicasCliente() {
  const { user, switchClinic } = useAuth();
  const { hasModulo } = useModulosSaas();
  const [clinicas, setClinicas] = useState<ClinicaSaas[]>([]);
  const [usuarios, setUsuarios] = useState<UsuarioClinica[]>([]);
  const [clientes, setClientes] = useState<ClienteSaas[]>([]);
  const [perfisAcesso, setPerfisAcesso] = useState<PerfilAcesso[]>([]);
  const [sessoesAtivas, setSessoesAtivas] = useState<SessaoAtiva[]>([]);
  const [funcionarios, setFuncionarios] = useState<Funcionario[]>([]);
  const [dentistas, setDentistas] = useState<Dentista[]>([]);
  const [loading, setLoading] = useState(true);
  const [clinicaDialog, setClinicaDialog] = useState(false);
  const [usuarioDialog, setUsuarioDialog] = useState(false);
  const [editingClinica, setEditingClinica] = useState<ClinicaSaas | null>(null);
  const [editingUsuario, setEditingUsuario] = useState<UsuarioClinica | null>(null);
  const [clinicaForm, setClinicaForm] = useState(emptyClinica);
  const [usuarioForm, setUsuarioForm] = useState(emptyUsuario);
  const [acessandoClinicaId, setAcessandoClinicaId] = useState("");
  const [autoCnpjEnabled, setAutoCnpjEnabled] = useState(false);
  const [autoCepEnabled, setAutoCepEnabled] = useState(false);

  const clienteId = user?.cliente_id || "default";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [clinicasRows, usuariosRows, funcionariosRows, dentistasRows, perfisRows, sessoesRows] = await Promise.all([
        apiList<ClinicaSaas>("clinicas"),
        apiList<UsuarioClinica>("usuarios_clinica"),
        apiList<Funcionario>("funcionarios"),
        apiList<Dentista>("dentistas"),
        apiList<PerfilAcesso>("perfis_acesso").catch(() => []),
        apiList<SessaoAtiva>("sessoes_ativas").catch(() => []),
      ]);
      const clientesRows = await apiList<ClienteSaas>("clientes_saas").catch(() => []);
      setClinicas(clinicasRows);
      setUsuarios(usuariosRows);
      setClientes(clientesRows);
      setFuncionarios(funcionariosRows.filter((funcionario) => (funcionario.status || "Ativo") === "Ativo"));
      setDentistas(dentistasRows.filter((dentista) => (dentista.status || "Ativo") === "Ativo"));
      setPerfisAcesso(perfisRows.filter((perfil) => perfil.cliente_id === clienteId && perfil.ativo !== false));
      setSessoesAtivas(sessoesRows.filter((sessao) => sessao.status === "Ativa"));
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Erro ao carregar clínicas");
    } finally {
      setLoading(false);
    }
  }, [clienteId]);

  useEffect(() => {
    load();
  }, [load]);

  const usuariosPorClinica = useMemo(() => {
    return usuarios.reduce<Record<string, UsuarioClinica[]>>((acc, usuario) => {
      const ids = usuario.clinicas_ids?.length ? usuario.clinicas_ids : [usuario.clinica_id];
      ids.filter(Boolean).forEach((clinicaId) => {
        acc[clinicaId] = acc[clinicaId] || [];
        acc[clinicaId].push(usuario);
      });
      return acc;
    }, {});
  }, [usuarios]);

  const openClinica = (clinica?: ClinicaSaas) => {
    setEditingClinica(clinica || null);
    setClinicaForm(clinica || { ...emptyClinica, cliente_id: clienteId });
    setAutoCnpjEnabled(false);
    setAutoCepEnabled(false);
    setClinicaDialog(true);
  };

  const cnpjLookup = useAutomaticBrazilLookup({
    value: clinicaForm.cnpj || "",
    digits: 14,
    enabled: autoCnpjEnabled,
    lookup: lookupCnpj,
    onSuccess: (info) => {
      setClinicaForm((prev) => ({
        ...prev,
        cnpj: info.cnpj,
        nome: prev.nome || info.nome_fantasia || info.razao_social,
        razao_social: info.razao_social,
        nome_fantasia: info.nome_fantasia,
        telefone: info.telefone || prev.telefone,
        email: info.email || prev.email,
        cep: info.cep,
        endereco: info.endereco,
        numero: info.numero,
        complemento: info.complemento,
        bairro: info.bairro,
        cidade: info.cidade,
        estado: info.estado,
        situacao_cadastral: info.situacao_cadastral,
        cnae_principal: info.cnae_principal,
        natureza_juridica: info.natureza_juridica,
        porte: info.porte,
        data_abertura: info.data_abertura,
      }));
      toast.success("Dados do CNPJ preenchidos automaticamente");
    },
    onError: (error) => toast.error(error.message || "Não foi possível consultar o CNPJ"),
  });

  const cepLookup = useAutomaticBrazilLookup({
    value: clinicaForm.cep || "",
    digits: 8,
    enabled: autoCepEnabled,
    lookup: lookupCep,
    onSuccess: (info) => {
      setClinicaForm((prev) => ({
        ...prev,
        cep: info.cep,
        endereco: info.logradouro || prev.endereco,
        complemento: info.complemento || prev.complemento,
        bairro: info.bairro || prev.bairro,
        cidade: info.cidade || prev.cidade,
        estado: info.estado || prev.estado,
      }));
      toast.success("Endereço preenchido pelo CEP");
    },
    onError: (error) => toast.error(error.message || "Não foi possível consultar o CEP"),
  });

  const openUsuario = (usuario?: UsuarioClinica, clinicaId?: string) => {
    setEditingUsuario(usuario || null);
    const selectedClinicas = usuario?.clinicas_ids?.length ? usuario.clinicas_ids : usuario?.clinica_id ? [usuario.clinica_id] : clinicaId ? [clinicaId] : clinicas[0]?.id ? [clinicas[0].id] : [];
    setUsuarioForm(
      usuario
        ? { ...usuario, clinicas_ids: selectedClinicas, clinica_id: selectedClinicas[0] || "", senha: "" }
        : { ...emptyUsuario, cliente_id: clienteId, clinica_id: selectedClinicas[0] || "", clinicas_ids: selectedClinicas }
    );
    setUsuarioDialog(true);
  };

  const saveClinica = async () => {
    if (!clinicaForm.nome.trim()) {
      toast.error("Informe o nome da clínica");
      return;
    }
    const payload = { ...clinicaForm, cliente_id: clienteId };
    if (editingClinica) await apiUpdate<ClinicaSaas>("clinicas", editingClinica.id, payload);
    else await apiCreate<ClinicaSaas>("clinicas", payload);
    toast.success("Clínica salva");
    setClinicaDialog(false);
    await load();
  };

  const handleClinicLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Selecione um arquivo de imagem");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("A logo deve ter no máximo 2 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setClinicaForm((current) => ({ ...current, logo_base64: String(reader.result || "") }));
    reader.readAsDataURL(file);
  };

  const saveUsuario = async () => {
    const selectedClinicas = usuarioForm.clinicas_ids?.length ? usuarioForm.clinicas_ids : usuarioForm.clinica_id ? [usuarioForm.clinica_id] : [];
    const email = normalizeEmail(usuarioForm.email);
    if (!usuarioForm.nome.trim() || !email || selectedClinicas.length === 0) {
      toast.error("Preencha nome, email e ao menos uma clínica");
      return;
    }
    const emailEmUsuario = usuarios.some((usuario) => usuario.id !== editingUsuario?.id && normalizeEmail(usuario.email) === email);
    const emailEmCliente = clientes.some((cliente) => normalizeEmail(cliente.email_responsavel) === email && cliente.login_user_id !== editingUsuario?.login_user_id);
    if (emailEmUsuario || emailEmCliente) {
      toast.error("Email já cadastrado");
      return;
    }

    const precisaCriarLogin = !editingUsuario?.login_user_id;
    if (precisaCriarLogin && !usuarioForm.senha) {
      toast.error(editingUsuario ? "Informe uma senha para criar o acesso deste usuário" : "Informe uma senha inicial para o usuário acessar o sistema");
      return;
    }

    let loginUserId = editingUsuario?.login_user_id;
    if (usuarioForm.senha || editingUsuario?.login_user_id) {
      const authResponse = await fetch("/api/auth-users.php?action=upsert", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nome: usuarioForm.nome,
          email,
          senha: usuarioForm.senha || "",
          papel: usuarioForm.papel,
          status: usuarioForm.status,
          cliente_id: clienteId,
          clinica_id: selectedClinicas[0],
          clinicas_ids: selectedClinicas,
          login_user_id: editingUsuario?.login_user_id,
          exigir_troca_senha: usuarioForm.exigir_troca_senha,
          mfa_obrigatorio: usuarioForm.mfa_obrigatorio,
        }),
      });
      const authPayload = await authResponse.json().catch(() => ({}));
      if (!authResponse.ok || authPayload.error) {
        toast.error(authPayload.error || "Erro ao salvar usuário de login");
        return;
      }
      loginUserId = authPayload.login_user_id || loginUserId;
    }

    const { senha, ...usuarioData } = usuarioForm;
    const permissoesExtra = sanitizeExtraPermissions(usuarioForm.permissoes_extra || []);
    const payload = {
      ...usuarioData,
      email,
      cliente_id: clienteId,
      clinica_id: selectedClinicas[0],
      clinicas_ids: selectedClinicas,
      login_user_id: loginUserId,
      permissoes_extra: permissoesExtra,
      permissoes_por_clinica: permissionsForSelectedClinics(
        selectedClinicas,
        permissoesExtra,
        usuarioForm.permissoes_por_clinica || {}
      ),
      exigir_troca_senha: usuarioForm.exigir_troca_senha === true,
      mfa_obrigatorio: usuarioForm.mfa_obrigatorio === true,
    };
    if (editingUsuario) await apiUpdate<UsuarioClinica>("usuarios_clinica", editingUsuario.id, payload);
    else await apiCreate<UsuarioClinica>("usuarios_clinica", payload);
    await registrarAuditoriaPermissao(editingUsuario, payload);

    toast.success(usuarioForm.senha ? "Usuário e senha salvos" : "Usuário salvo");
    setUsuarioDialog(false);
    await load();
  };

  const acessarClinica = async (clinica: ClinicaSaas) => {
    if (acessandoClinicaId) return;
    setAcessandoClinicaId(clinica.id);
    const { error, clinica: selectedClinic } = await switchClinic(clinica.id);
    if (error || !selectedClinic) {
      toast.error(error?.message || "Não foi possível acessar esta clínica");
      setAcessandoClinicaId("");
      return;
    }
    toast.success(`Clínica ativa: ${selectedClinic.nome}`);
    window.location.assign("/");
  };

  const toggleUsuarioClinica = (clinicaId: string, checked: boolean) => {
    const current = usuarioForm.clinicas_ids?.length ? usuarioForm.clinicas_ids : usuarioForm.clinica_id ? [usuarioForm.clinica_id] : [];
    const next = checked ? Array.from(new Set([...current, clinicaId])) : current.filter((id) => id !== clinicaId);
    const currentMap = usuarioForm.permissoes_por_clinica || {};
    const nextMap = checked
      ? permissionsForSelectedClinics(next, sanitizeExtraPermissions(usuarioForm.permissoes_extra || []), currentMap)
      : Object.fromEntries(Object.entries(currentMap).filter(([id]) => id !== clinicaId));
    setUsuarioForm({ ...usuarioForm, clinicas_ids: next, clinica_id: next[0] || "", permissoes_por_clinica: nextMap });
  };

  const togglePermissaoExtra = (permission: string, checked: boolean) => {
    const current = sanitizeExtraPermissions(usuarioForm.permissoes_extra || []);
    const next = checked ? Array.from(new Set([...current, permission])) : current.filter((item) => item !== permission);
    const selectedClinicas = usuarioForm.clinicas_ids?.length ? usuarioForm.clinicas_ids : usuarioForm.clinica_id ? [usuarioForm.clinica_id] : [];
    const currentMap = usuarioForm.permissoes_por_clinica || {};
    const nextMap = selectedClinicas.reduce<Record<string, string[]>>((map, clinicaId) => {
      const scoped = sanitizeExtraPermissions(currentMap[clinicaId] || []);
      map[clinicaId] = checked
        ? sanitizeExtraPermissions([...scoped, permission])
        : scoped.filter((item) => item !== permission);
      return map;
    }, {});
    setUsuarioForm({ ...usuarioForm, permissoes_extra: next, permissoes_por_clinica: nextMap });
  };

  const aplicarPreset = (preset: keyof typeof permissionPresets, papel: UserRole) => {
    const selectedClinicas = usuarioForm.clinicas_ids?.length ? usuarioForm.clinicas_ids : usuarioForm.clinica_id ? [usuarioForm.clinica_id] : [];
    const permittedPreset = permissionPresets[preset].filter((permission) => permissionModuleAllowed(permission));
    const nextPermissions = sanitizeExtraPermissions([
      ...(usuarioForm.permissoes_extra || []),
      ...permittedPreset,
    ]);
    setUsuarioForm({
      ...usuarioForm,
      papel,
      permissoes_extra: nextPermissions,
      permissoes_por_clinica: permissionsForSelectedClinics(
        selectedClinicas,
        nextPermissions,
        usuarioForm.permissoes_por_clinica || {}
      ),
    });
    toast.success("Perfil aplicado a todas as unidades liberadas");
  };

  const hasBasePermission = (permission: string) => {
    const permissions = rolePermissions[usuarioForm.papel] || [];
    return permissions.includes("*") || permissions.includes(permission);
  };

  const togglePermissaoClinica = (clinicaId: string, permission: string, checked: boolean) => {
    const currentMap = usuarioForm.permissoes_por_clinica || {};
    const current = sanitizeExtraPermissions(currentMap[clinicaId] || []);
    const next = checked ? Array.from(new Set([...current, permission])) : current.filter((item) => item !== permission);
    setUsuarioForm({
      ...usuarioForm,
      permissoes_por_clinica: {
        ...currentMap,
        [clinicaId]: next,
      },
    });
  };

  const permissionModuleAllowed = (permission: string) => {
    const modulo = delegablePermissions.find((item) => item.key === permission)?.modulo;
    return !modulo || hasModulo(modulo);
  };

  const aplicarPerfil = (perfilId: string) => {
    const perfil = perfisAcesso.find((item) => item.id === perfilId);
    if (!perfil) {
      setUsuarioForm({ ...usuarioForm, perfil_personalizado_id: "" });
      return;
    }
    setUsuarioForm({
      ...usuarioForm,
      perfil_personalizado_id: perfil.id,
      papel: perfil.papel_base,
      permissoes_extra: sanitizeExtraPermissions(perfil.permissoes_extra || []),
      permissoes_por_clinica: perfil.permissoes_por_clinica || {},
    });
  };

  const salvarPerfilAtual = async () => {
    const nome = window.prompt("Nome do perfil personalizado");
    if (!nome?.trim()) return;
    await apiCreate<PerfilAcesso>("perfis_acesso", {
      cliente_id: clienteId,
      nome: nome.trim(),
      papel_base: usuarioForm.papel,
      permissoes_extra: sanitizeExtraPermissions(usuarioForm.permissoes_extra || []),
      permissoes_por_clinica: usuarioForm.permissoes_por_clinica || {},
      ativo: true,
    });
    toast.success("Perfil personalizado salvo");
    await load();
  };

  const registrarAuditoriaPermissao = async (usuarioAntes: UsuarioClinica | null, usuarioDepois: Record<string, unknown>) => {
    await apiCreate("historico_permissoes", {
      cliente_id: clienteId,
      usuario_id: usuarioAntes?.id || usuarioDepois.id || "",
      usuario_email: usuarioDepois.email,
      usuario_nome: usuarioDepois.nome,
      antes: usuarioAntes ? {
        papel: usuarioAntes.papel,
        clinicas_ids: usuarioAntes.clinicas_ids || [],
        permissoes_extra: usuarioAntes.permissoes_extra || [],
        permissoes_por_clinica: usuarioAntes.permissoes_por_clinica || {},
      } : null,
      depois: {
        papel: usuarioDepois.papel,
        clinicas_ids: usuarioDepois.clinicas_ids,
        permissoes_extra: usuarioDepois.permissoes_extra,
        permissoes_por_clinica: usuarioDepois.permissoes_por_clinica,
      },
      motivo: usuarioAntes ? "Atualizacao de acesso pelo admin da clinica" : "Criacao de acesso pelo admin da clinica",
    }).catch(() => undefined);
  };

  const enviarConvite = async (usuario: UsuarioClinica) => {
    const token = `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    const link = `${window.location.origin}/auth?convite=${encodeURIComponent(token)}`;
    await apiCreate("convites_acesso", {
      cliente_id: clienteId,
      usuario_id: usuario.id,
      usuario_email: usuario.email,
      usuario_nome: usuario.nome,
      token,
      link,
      status: "Pendente",
      expira_em: new Date(Date.now() + 1000 * 60 * 60 * 24 * 7).toISOString(),
    });
    await apiCreate("fila_mensagens", {
      cliente_id: clienteId,
      clinica_id: usuario.clinica_id,
      canal: "email",
      tipo: "convite_acesso",
      destinatario: usuario.email,
      assunto: "Convite de acesso ao Cloud Odonto",
      mensagem: `Olá, ${usuario.nome}. Acesse ${link} para definir sua senha de acesso.`,
      status: "Pendente",
    }).catch(() => undefined);
    await apiUpdate<UsuarioClinica>("usuarios_clinica", usuario.id, { convite_enviado_em: new Date().toISOString() });
    toast.success("Convite gerado e colocado na fila de email");
    await load();
  };

  const encerrarSessao = async (sessao: SessaoAtiva) => {
    await apiUpdate<SessaoAtiva>("sessoes_ativas", sessao.id, { status: "Encerrada", encerrada_em: new Date().toISOString() });
    toast.success("Sessão marcada como encerrada");
    await load();
  };

  const selectFuncionario = (funcionarioId: string) => {
    if (funcionarioId === "manual") {
      setUsuarioForm({ ...usuarioForm, funcionario_id: "" });
      return;
    }
    const funcionario = funcionarios.find((item) => item.id === funcionarioId);
    if (!funcionario) return;
    setUsuarioForm({
      ...usuarioForm,
      funcionario_id: funcionario.id,
      dentista_id: "",
      nome: funcionario.nome,
      email: funcionario.email || usuarioForm.email,
    });
  };

  const selectDentista = (dentistaId: string) => {
    if (dentistaId === "manual") {
      setUsuarioForm({ ...usuarioForm, dentista_id: "" });
      return;
    }
    const dentista = dentistas.find((item) => item.id === dentistaId);
    if (!dentista) return;
    setUsuarioForm({
      ...usuarioForm,
      dentista_id: dentista.id,
      funcionario_id: "",
      nome: dentista.nome,
      email: dentista.email || usuarioForm.email,
      papel: "dentista",
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Minhas Clínicas</h1>
            <p className="text-sm text-muted-foreground">
              Cadastre unidades e vincule usuários a cada clínica.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => openUsuario()} disabled={clinicas.length === 0}>
              <UserPlus className="mr-2 h-4 w-4" />
              Usuário
            </Button>
            <Button onClick={() => openClinica()}>
              <Plus className="mr-2 h-4 w-4" />
              Clínica
            </Button>
          </div>
        </div>

        {loading ? (
          <Card>
            <CardContent className="p-6 text-sm text-muted-foreground">Carregando...</CardContent>
          </Card>
        ) : clinicas.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-sm text-muted-foreground">
              Nenhuma clínica cadastrada para este cliente.
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {clinicas.map((clinica) => (
              <Card key={clinica.id}>
                <CardHeader className="flex flex-row items-start justify-between gap-4">
                  <div className="space-y-1">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Building2 className="h-5 w-5 text-primary" />
                      {clinica.nome}
                      <Badge variant={clinica.status === "Ativa" ? "default" : "secondary"}>{clinica.status}</Badge>
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {[clinica.cnpj, clinica.cidade, clinica.estado].filter(Boolean).join(" - ") || "Dados cadastrais pendentes"}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => acessarClinica(clinica)} disabled={!!acessandoClinicaId}>
                      {acessandoClinicaId === clinica.id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {acessandoClinicaId === clinica.id ? "Acessando" : "Acessar"}
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openClinica(clinica)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium">Usuários vinculados</h3>
                      <Button variant="outline" size="sm" onClick={() => openUsuario(undefined, clinica.id)}>
                        <Plus className="mr-2 h-4 w-4" />
                        Usuário
                      </Button>
                    </div>
                    {(usuariosPorClinica[clinica.id] || []).length === 0 ? (
                      <p className="text-sm text-muted-foreground">Nenhum usuário vinculado a esta clínica.</p>
                    ) : (
                      <div className="grid gap-2">
                        {usuariosPorClinica[clinica.id].map((usuario) => (
                          <div key={usuario.id} className="flex items-center justify-between rounded-md border p-3">
                            <div>
                              <p className="font-medium">{usuario.nome}</p>
                              <p className="text-sm text-muted-foreground">
                                {usuario.email} - {roleLabels[usuario.papel] || usuario.papel}
                              </p>
                              <div className="mt-2 flex flex-wrap gap-1">
                                {(usuario.permissoes_extra || []).slice(0, 4).map((permissao) => (
                                  <Badge key={permissao} variant="secondary">{permissao}</Badge>
                                ))}
                                {usuario.mfa_obrigatorio && <Badge variant="outline">MFA</Badge>}
                                {usuario.exigir_troca_senha && <Badge variant="outline">Troca senha</Badge>}
                              </div>
                              {usuario.funcionario_id && (
                                <p className="text-xs text-muted-foreground">Vinculado ao cadastro de funcionário</p>
                              )}
                              {usuario.dentista_id && (
                                <p className="text-xs text-muted-foreground">Vinculado ao cadastro de dentista</p>
                              )}
                            </div>
                            <div className="flex flex-wrap justify-end gap-2">
                              <Button variant="outline" size="sm" onClick={() => enviarConvite(usuario)}>
                                <Mail className="mr-2 h-4 w-4" />
                                Convite
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => openUsuario(usuario)}>
                                Transferir/editar
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <ShieldCheck className="h-5 w-5" />
                Perfis personalizados
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {perfisAcesso.length === 0 ? (
                <p className="text-muted-foreground">Nenhum perfil salvo. Edite um usuário, ajuste as permissões e salve como modelo.</p>
              ) : (
                perfisAcesso.map((perfil) => (
                  <div key={perfil.id} className="rounded-md border p-3">
                    <p className="font-medium">{perfil.nome}</p>
                    <p className="text-muted-foreground">{roleLabels[perfil.papel_base]} - {(perfil.permissoes_extra || []).length} permissões extras</p>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <KeyRound className="h-5 w-5" />
                Sessões ativas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {sessoesAtivas.length === 0 ? (
                <p className="text-muted-foreground">Nenhuma sessão ativa registrada.</p>
              ) : (
                sessoesAtivas.slice(0, 6).map((sessao) => (
                  <div key={sessao.id} className="flex items-center justify-between gap-3 rounded-md border p-3">
                    <div>
                      <p className="font-medium">{sessao.usuario_nome || sessao.usuario_email}</p>
                      <p className="text-xs text-muted-foreground">{sessao.ip || "IP não registrado"}</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => encerrarSessao(sessao)}>
                      Encerrar
                    </Button>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={clinicaDialog} onOpenChange={setClinicaDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editingClinica ? "Editar clínica" : "Nova clínica"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input value={clinicaForm.nome} onChange={(e) => setClinicaForm({ ...clinicaForm, nome: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>CNPJ</Label>
              <Input value={clinicaForm.cnpj || ""} onChange={(e) => { setAutoCnpjEnabled(true); setClinicaForm({ ...clinicaForm, cnpj: maskCnpj(e.target.value) }); }} placeholder="00.000.000/0000-00" />
              {cnpjLookup.loading && <p className="text-xs text-muted-foreground">Consultando dados na Receita Federal...</p>}
            </div>
            <div className="space-y-2">
              <Label>Razão social</Label>
              <Input value={clinicaForm.razao_social || ""} onChange={(e) => setClinicaForm({ ...clinicaForm, razao_social: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Nome fantasia</Label>
              <Input value={clinicaForm.nome_fantasia || ""} onChange={(e) => setClinicaForm({ ...clinicaForm, nome_fantasia: e.target.value })} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Logo da empresa</Label>
              <div className="flex flex-col gap-3 rounded-md border p-3 sm:flex-row sm:items-center">
                <div className="flex h-20 w-32 shrink-0 items-center justify-center rounded-md border bg-background p-2">
                  {clinicaForm.logo_base64 ? <img src={clinicaForm.logo_base64} alt="Logo da empresa" className="h-full w-full object-contain" /> : <ImageIcon className="h-8 w-8 text-muted-foreground" />}
                </div>
                <div className="flex min-w-0 flex-1 flex-wrap gap-2">
                  <Button type="button" variant="outline" asChild><label className="cursor-pointer"><ImageIcon className="mr-2 h-4 w-4" />Selecionar logo<input type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={handleClinicLogoUpload} /></label></Button>
                  {clinicaForm.logo_base64 && <Button type="button" variant="outline" onClick={() => setClinicaForm({ ...clinicaForm, logo_base64: "" })}><Trash2 className="mr-2 h-4 w-4" />Remover</Button>}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Essa marca identifica a unidade em telas e documentos. A assinatura Cloud Odonto continuará aparecendo como plataforma do sistema.</p>
            </div>
            <div className="space-y-2">
              <Label>Telefone</Label>
              <Input value={clinicaForm.telefone || ""} onChange={(e) => setClinicaForm({ ...clinicaForm, telefone: maskPhone(e.target.value) })} />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={clinicaForm.email || ""} onChange={(e) => setClinicaForm({ ...clinicaForm, email: e.target.value })} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Endereço</Label>
              <Input value={clinicaForm.endereco || ""} onChange={(e) => setClinicaForm({ ...clinicaForm, endereco: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>CEP</Label>
              <Input value={clinicaForm.cep || ""} onChange={(e) => { setAutoCepEnabled(true); setClinicaForm({ ...clinicaForm, cep: maskCep(e.target.value) }); }} placeholder="00000-000" />
              {cepLookup.loading && <p className="text-xs text-muted-foreground">Consultando CEP...</p>}
            </div>
            <div className="space-y-2">
              <Label>Número</Label>
              <Input value={clinicaForm.numero || ""} onChange={(e) => setClinicaForm({ ...clinicaForm, numero: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Complemento</Label>
              <Input value={clinicaForm.complemento || ""} onChange={(e) => setClinicaForm({ ...clinicaForm, complemento: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Bairro</Label>
              <Input value={clinicaForm.bairro || ""} onChange={(e) => setClinicaForm({ ...clinicaForm, bairro: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Cidade</Label>
              <Input value={clinicaForm.cidade || ""} onChange={(e) => setClinicaForm({ ...clinicaForm, cidade: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Estado</Label>
              <Input value={clinicaForm.estado || ""} onChange={(e) => setClinicaForm({ ...clinicaForm, estado: e.target.value.toUpperCase().slice(0, 2) })} />
            </div>
            {(clinicaForm.situacao_cadastral || clinicaForm.cnae_principal) && <div className="grid gap-2 rounded-md border bg-muted/20 p-3 text-sm md:col-span-2 md:grid-cols-2"><p><strong>Situação:</strong> {clinicaForm.situacao_cadastral || "-"}</p><p><strong>Abertura:</strong> {clinicaForm.data_abertura || "-"}</p><p><strong>Atividade:</strong> {clinicaForm.cnae_principal || "-"}</p><p><strong>Natureza/porte:</strong> {[clinicaForm.natureza_juridica, clinicaForm.porte].filter(Boolean).join(" - ") || "-"}</p></div>}
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={clinicaForm.status} onValueChange={(value: ClinicaStatus) => setClinicaForm({ ...clinicaForm, status: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Ativa">Ativa</SelectItem>
                  <SelectItem value="Inativa">Inativa</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Observações</Label>
              <Textarea value={clinicaForm.observacoes || ""} onChange={(e) => setClinicaForm({ ...clinicaForm, observacoes: e.target.value })} />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setClinicaDialog(false)}>Cancelar</Button>
            <Button onClick={saveClinica}>Salvar</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={usuarioDialog} onOpenChange={setUsuarioDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingUsuario ? "Editar usuário" : "Novo usuário"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2 md:col-span-2">
              <Label>Funcionário</Label>
              <Select value={usuarioForm.funcionario_id || "manual"} onValueChange={selectFuncionario}>
                <SelectTrigger><SelectValue placeholder="Selecione um funcionário" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Acesso sem funcionário vinculado</SelectItem>
                  {funcionarios.map((funcionario) => (
                    <SelectItem key={funcionario.id} value={funcionario.id}>
                      {funcionario.nome}{funcionario.cargo ? ` - ${funcionario.cargo}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Dentista</Label>
              <Select value={usuarioForm.dentista_id || "manual"} onValueChange={selectDentista}>
                <SelectTrigger><SelectValue placeholder="Selecione um dentista" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Acesso sem dentista vinculado</SelectItem>
                  {dentistas.map((dentista) => (
                    <SelectItem key={dentista.id} value={dentista.id}>
                      {dentista.nome}{dentista.especialidade ? ` - ${dentista.especialidade}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input value={usuarioForm.nome} onChange={(e) => setUsuarioForm({ ...usuarioForm, nome: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={usuarioForm.email} onChange={(e) => setUsuarioForm({ ...usuarioForm, email: e.target.value })} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Clínicas liberadas</Label>
              <div className="grid gap-2 rounded-md border p-3 md:grid-cols-2">
                {clinicas.map((clinica) => {
                  const selected = (usuarioForm.clinicas_ids?.length ? usuarioForm.clinicas_ids : usuarioForm.clinica_id ? [usuarioForm.clinica_id] : []).includes(clinica.id);
                  return (
                    <label key={clinica.id} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={selected} onCheckedChange={(checked) => toggleUsuarioClinica(clinica.id, checked === true)} />
                      <span>{clinica.nome}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Perfil personalizado</Label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Select value={usuarioForm.perfil_personalizado_id || "manual"} onValueChange={aplicarPerfil}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Sem perfil personalizado</SelectItem>
                    {perfisAcesso.map((perfil) => (
                      <SelectItem key={perfil.id} value={perfil.id}>{perfil.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" onClick={salvarPerfilAtual}>
                  Salvar modelo
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Papel</Label>
              <Select value={usuarioForm.papel} onValueChange={(value: UserRole) => setUsuarioForm({ ...usuarioForm, papel: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(roleLabels).filter(([role]) => role !== "superadmin").map(([role, label]) => (
                    <SelectItem key={role} value={role}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={usuarioForm.status} onValueChange={(value: UsuarioStatus) => setUsuarioForm({ ...usuarioForm, status: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Ativo">Ativo</SelectItem>
                  <SelectItem value="Bloqueado">Bloqueado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-3 md:col-span-2">
              <div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <Label>Permissões extras</Label>
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => aplicarPreset("recepcao", "recepcao")}><ShieldCheck className="mr-2 h-4 w-4" />Recepção</Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => aplicarPreset("dentista", "dentista")}>Dentista</Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => aplicarPreset("financeiro", "financeiro")}>Financeiro</Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => aplicarPreset("gerencia", "gerente_clinica")}>Gerência</Button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  As permissões extras são aplicadas automaticamente a todas as unidades liberadas, sem conceder acesso de dono da conta ou SuperAdmin.
                </p>
              </div>
              <div className="grid gap-2 rounded-md border p-3 md:grid-cols-2">
                {delegablePermissions.map((permission) => {
                  const base = hasBasePermission(permission.key);
                  const selected = sanitizeExtraPermissions(usuarioForm.permissoes_extra || []).includes(permission.key);
                  const moduloAllowed = permissionModuleAllowed(permission.key);
                  return (
                    <label key={permission.key} className={`flex items-start gap-2 rounded-md p-2 text-sm ${base || !moduloAllowed ? "bg-muted/40 text-muted-foreground" : ""}`}>
                      <Checkbox
                        checked={base || selected}
                        disabled={base || !moduloAllowed}
                        onCheckedChange={(checked) => togglePermissaoExtra(permission.key, checked === true)}
                      />
                      <span>
                        <span className="font-medium">{permission.label}</span>
                        {base && <span className="ml-1 text-xs">(já incluso no perfil)</span>}
                        {!moduloAllowed && <span className="ml-1 text-xs">(módulo não contratado)</span>}
                        <span className="block text-xs text-muted-foreground">{permission.description}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="space-y-3 md:col-span-2">
              <div>
                <Label>Permissões por unidade</Label>
                <p className="text-xs text-muted-foreground">Use quando o usuário precisa de uma permissão apenas em uma clínica específica.</p>
              </div>
              <div className="space-y-3 rounded-md border p-3">
                {(usuarioForm.clinicas_ids?.length ? usuarioForm.clinicas_ids : usuarioForm.clinica_id ? [usuarioForm.clinica_id] : []).map((clinicaId) => {
                  const clinica = clinicas.find((item) => item.id === clinicaId);
                  const selected = sanitizeExtraPermissions(usuarioForm.permissoes_por_clinica?.[clinicaId] || []);
                  return (
                    <div key={clinicaId} className="space-y-2">
                      <p className="text-sm font-medium">{clinica?.nome || "Clínica"}</p>
                      <div className="grid gap-2 md:grid-cols-3">
                        {delegablePermissions.filter((permission) => permissionModuleAllowed(permission.key)).map((permission) => (
                          <label key={`${clinicaId}-${permission.key}`} className="flex items-center gap-2 text-xs">
                            <Checkbox
                              checked={selected.includes(permission.key)}
                              onCheckedChange={(checked) => togglePermissaoClinica(clinicaId, permission.key, checked === true)}
                            />
                            <span>{permission.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="space-y-3 md:col-span-2">
              <Label>Segurança do acesso</Label>
              <div className="grid gap-2 rounded-md border p-3 md:grid-cols-2">
                <label className="flex items-start gap-2 text-sm">
                  <Checkbox checked={usuarioForm.exigir_troca_senha === true} onCheckedChange={(checked) => setUsuarioForm({ ...usuarioForm, exigir_troca_senha: checked === true })} />
                  <span>
                    <span className="font-medium">Trocar senha no primeiro login</span>
                    <span className="block text-xs text-muted-foreground">Obrigatório para senha inicial definida pelo admin.</span>
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm">
                  <Checkbox checked={usuarioForm.mfa_obrigatorio === true} onCheckedChange={(checked) => setUsuarioForm({ ...usuarioForm, mfa_obrigatorio: checked === true })} />
                  <span>
                    <span className="font-medium">Exigir MFA para este usuário</span>
                    <span className="block text-xs text-muted-foreground">Opcional. Use principalmente para admins e financeiro.</span>
                  </span>
                </label>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{editingUsuario?.login_user_id ? "Nova senha" : "Senha inicial obrigatória"}</Label>
              <Input type="password" value={usuarioForm.senha || ""} onChange={(e) => setUsuarioForm({ ...usuarioForm, senha: e.target.value })} />
              <p className="text-xs text-muted-foreground">
                {editingUsuario?.login_user_id
                  ? "Preencha somente se quiser resetar a senha deste acesso."
                  : "Sem senha inicial o usuário não consegue fazer login."}
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setUsuarioDialog(false)}>Cancelar</Button>
            <Button onClick={saveUsuario}>Salvar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
